import React from 'react';
import { rem } from 'polished';
import styled, { css, useTheme } from 'styled-components';
import { BodyText, extraSmall } from '@unmind/design-system';
import { isMSTeams } from 'utils/MSTeams';
import { IconProps } from 'icons';
import { NavLink } from 'react-router-dom';
import { MainMenuFeatures } from '../MainMenu';

const Wrapper = styled.nav<{ isInFooter: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${p => (p.isInFooter ? 'space-evenly' : 'flex-start')};
  flex-wrap: wrap;
  padding-top: ${p => (p.isInFooter ? rem(12) : rem(0))};
  padding-bottom: ${p => (p.isInFooter ? rem(12) : rem(0))};
  height: ${p => (p.isInFooter ? 'auto' : rem(48))};

  ${extraSmall(css`
    height: auto;
    justify-content: 'center';
    flex-wrap: nowrap;
  `)}
`;

const MenuLink = styled(NavLink)<{
  $isInFooter?: boolean;
  $isSelected?: boolean;
}>`
  text-decoration: none;
  display: flex;
  flex-direction: ${({ $isInFooter }) => ($isInFooter ? 'column' : 'row')};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: max-content;
  position: relative;

  ${extraSmall(css`
    margin-left: ${rem('12px')};
    margin-right: ${rem('12px')};
  `)}

  color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.mainNavigation.tab.active.text
      : theme.colors.mainNavigation.tab.inactive.text};

  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
  }

  &:hover:after {
    border-top: ${({ $isInFooter, $isSelected }) =>
      $isInFooter
        ? 'none'
        : css`
            ${rem('2px')} solid ${({ theme }) =>
              $isSelected
                ? theme.colors.mainNavigation.tab.active.text
                : theme.colors.text.secondary}
          `};
    transition: border-top 100ms ease-in;
  }

  ${props =>
    props.$isSelected &&
    css`
      color: ${({ theme }) => theme.colors.mainNavigation.tab.active.text};

      span {
        color: ${({ theme }) => theme.colors.mainNavigation.tab.active.text};
      }
    `}

  ${props =>
    props.$isSelected &&
    extraSmall(css`
      &::after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);

        width: 100%;
        border-top: ${rem('2px')} solid
          ${({ theme }) => theme.colors.mainNavigation.tab.active.text};
      }
    `)}
`;

const LinkTitle = styled(BodyText).attrs(({ theme }) => ({
  sizes: [
    theme.typography.fontSizes.fontSize14,
    theme.typography.fontSizes.fontSize14,
    theme.typography.fontSizes.fontSize16,
  ],
}))`
  position: relative;
  padding-top: ${rem(8)};
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  color: ${({ theme }) => theme.colors.mainNavigation.tab.inactive.text};
`;

const MenuItem = styled.div<{ displayedInMSTeams: boolean }>`
  display: flex;
  justify-content: flex-start;
  margin-right: ${({ displayedInMSTeams }) =>
    displayedInMSTeams ? rem(10) : rem(0)};
  width: max-content;
  min-width: ${({ displayedInMSTeams }) =>
    displayedInMSTeams ? rem(0) : rem(60)};
  justify-content: center;

  &.active span {
    color: ${({ theme }) => theme.colors.mainNavigation.tab.active.text};
  }
`;

export interface LinkConfig {
  name: string;
  to: string;
  Icon: React.FC<IconProps>;
  shouldHide?: boolean;
  feature?: MainMenuFeatures;
  label?: string;
}

export interface HorizontalMenuProps {
  links: LinkConfig[];
  activeFeature?: MainMenuFeatures;
  onFeatureClick(feature?: MainMenuFeatures): void;
  isInFooter: boolean;
}

const HorizontalMenu = ({
  links,
  activeFeature,
  onFeatureClick,
  isInFooter,
}: HorizontalMenuProps) => {
  const theme = useTheme();

  return (
    <Wrapper isInFooter={isInFooter}>
      {links
        .filter(({ shouldHide }) => !shouldHide)
        .map(({ name, to, Icon, feature }) => {
          const isActive = feature === activeFeature;
          const iconColor = isActive
            ? theme.colors.mainNavigation.tab.active.icon
            : theme.colors.mainNavigation.tab.inactive.icon;

          return (
            <MenuItem key={name} displayedInMSTeams={isMSTeams()}>
              <MenuLink
                to={to}
                data-testid={`${feature}-menu-link`}
                $isInFooter={isInFooter}
                onClick={() => onFeatureClick(feature)}
                $isSelected={isActive}
              >
                {isInFooter ? (
                  <Icon
                    data-testid={`${feature}-icon`}
                    primaryColor={iconColor}
                    secondaryColor={iconColor}
                    width={24}
                    height={24}
                  />
                ) : null}
                <LinkTitle>{name}</LinkTitle>
              </MenuLink>
            </MenuItem>
          );
        })}
    </Wrapper>
  );
};

export default HorizontalMenu;
